import Vue from 'vue'
import VueRouter from 'vue-router'
import firebase from 'firebase/app'
import SiteIndex from '../views/index/SiteIndex.vue'

Vue.use(VueRouter)

/*
** Set auth
*/

const firebaseAuth = firebase.auth();

/*
** Make the page scroll to top for all route navigations
*/

const scrollBehavior = (to, from, savedPosition) => {

    // Check if nav element is hashtag
    if(to.hash) {

        return {

            selector: to.hash

        }

    }

    return { x: 0, y: 0 }

};

/*
** Set up the routes
*/

const routes = [

    {
        path: '/',
        name: 'SiteIndex',
        component: SiteIndex,
        meta: {
            isLoggedIn: true
        }
    },
    {
        path: '/sign-up',
        name: 'SignUp',
        component: () => import('../views/sisu/SignUp.vue'),
        meta: {
            isLoggedIn: true
        }
    },
    {
        path: '/sign-in',
        name: 'SignIn',
        component: () => import('../views/sisu/SignIn.vue'),
        meta: {
            isLoggedIn: true
        }
    },
    {
        path: '/forgot-password',
        name: 'ForgotPassword',
        component: () => import('../views/sisu/ForgotPassword.vue'),
        meta: {
            isLoggedIn: true
        }
    },
    {
        path: '/dashboard',
        name: 'Dashboard',
        component: () => import('../views/dashboard/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/properties/dashboard',
        name: 'PropertiesDashboard',
        component: () => import('../views/properties/dashboard/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/property/add',
        name: 'AddProperty',
        component: () => import('../views/property/add/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/property/:id/details',
        name: 'PropertyDetails',
        component: () => import('../views/property/details/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/property/:id/residents/:residentID',
        name: 'Resident',
        component: () => import('../views/property/resident/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/property/:id/residents',
        name: 'PropertyResidents',
        component: () => import('../views/property/residents/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/property/:id/units',
        name: 'PropertyUnits',
        component: () => import('../views/property/units/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/property/:id/units/:unitID',
        name: 'Unit',
        component: () => import('../views/property/unit/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/property/:id/resident/:residentID/leases/:leaseID/overview',
        name: 'LeaseRent',
        component: () => import('../views/property/lease/rent/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/settings/account',
        name: 'SettingsAccount',
        component: () => import('../views/settings/account/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/settings/company',
        name: 'SettingsCompany',
        component: () => import('../views/settings/company/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/settings/company/add',
        name: 'AddCompany',
        component: () => import('../views/settings/company/add/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/settings/residents-site',
        name: 'SettingsResidents',
        component: () => import('../views/settings/residents/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/settings/payments',
        name: 'SettingsPayments',
        component: () => import('../views/settings/payments/Index.vue'),
        meta: {
            requiresAuth: true
        }
    },
    {
        path: '/policies/policy/:slug',
        name: 'Policy',
        component: () => import('../views/policies/policy/Index.vue')
    },
    {
        path: '/blog',
        name: 'BlogIndex',
        component: () => import('../views/blog/Index.vue')
    },
    {
        path: '/blog/:slug',
        name: 'BlogCategory',
        component: () => import('../views/blog/category/Index.vue')
    },
    {
        path: '/blog/:category/:slug',
        name: 'BlogArticle',
        component: () => import('../views/blog/article/Index.vue')
    },
    {
        path: '/faq',
        name: 'FAQ',
        component: () => import('../views/support/faq/Index.vue')
    },
    {
        path: '/accessibility',
        name: 'Accessibility',
        component: () => import('../views/support/accessibility/Index.vue')
    },
    {
        path: '/help',
        name: 'Help',
        component: () => import('../views/support/Index.vue')
    },
    {
        path: '/help/:slug',
        name: 'HelpCategory',
        component: () => import('../views/support/category/Index.vue')
    },
    {
        path: '/help/:categorySlug/:slug',
        name: 'HelpArticle',
        component: () => import('../views/support/article/Index.vue')
    },
    {
        path: '/page-not-found',
        name: 'NotFound',
        component: () => import('../views/not-found/Index.vue')
    },
    {
        path: '*',
        redirect: '/page-not-found'
    },

]

const router = new VueRouter({

    /*
    ** Remove hashtag from URL
    */

    mode: 'history',

    /*
    ** Set the scroll behavior
    */

    scrollBehavior,

    /*
    ** Set the base URL
    */

    base: process.env.BASE_URL,

    /*
    ** Set the routes
    */

    routes

});

/*
** Loop through each route and apply the guards
*/

router.beforeEach((to, from, next) => {

    // Check to see if route requires auth
    if(to.matched.some(rec => rec.meta.requiresAuth)){

        // Get current auth'd user
        let user = firebaseAuth.currentUser;

        // Now move forward
        if (user) {

            // User signed in, proceed to route
            next();

        } else {

            // No user signed in, redirect to login page
            next({ name: 'SignIn' });

        }

    } else if(to.matched.some(rec => rec.meta.isLoggedIn)) {

        // Get current auth'd user
        let user = firebaseAuth.currentUser;

        // If logged in
        if(user) {

            // User signed in, proceed to user dashboard
            next({ name: 'Dashboard' });

        } else {

            // No user signed in, keep moving on
            next();

        }

    } else {

        // Do your thing
        next();

    }

});

export default router
